import { cloneDeep, isEqual } from "lodash";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isMaintenanceTask, isStatusTypeCompleted } from "../add-user-task";
import { SET_USER_TASK_FORM_DATA } from "../reducers/user-task-list-reducer";
import {
  getUserPermissionObject,
  hasAnyFeatureGroupAccess,
  TASK_GROUPS,
} from "../../user-permissions-helper";
import { USER_TYPE } from "../constant";

//this hook contains common functions used in user task list components
export function useTaskCommonFunctions() {
  const dispatch = useDispatch();
  const auditorList = useSelector(
    (state: any) => state["userTaskListReducer"].auditorList,
    isEqual,
  );

  const userTaskFormData = useSelector(
    (state: any) => state["userTaskListReducer"].userTaskFormData,
    isEqual
  );
  const userTaskStatusList = useSelector(
    (state: any) => state["userTaskListReducer"].userTaskStatusList,
    isEqual,
  );
  const currentUserId = useSelector(
    (state: any) => state["userTaskListReducer"].currentUserId,
    isEqual,
  );
  const currentUser = useSelector(
    (state) => state["mdaeNavigationBarReducer"].currentUser,
    isEqual,
  );

  const qaReviewPermission = getUserPermissionObject(
    "ALL_AUDITS_FOR_QA_REVIEW||MY_AUDITS_FOR_QA_REVIEW",
    currentUser,
  );
  const taskGroupPermission = getUserPermissionObject(
    "TASK_GROUPS",
    currentUser,
  );

  const hasTaskGroupAccessOnly =
    taskGroupPermission.hasUpdateAccess &&
    !(
      qaReviewPermission.hasAllAuditsForQaReviewUpdateAccess &&
      qaReviewPermission.hasMyAuditsForQaReviewUpdateAccess
    );
  const hasTaskGroupAccess = useMemo(
    () => hasAnyFeatureGroupAccess([TASK_GROUPS], currentUser),
    [currentUser],
  );
  const assignedToUserListFromAttachedLevel = useSelector(
    (state: any) => state["userTaskListReducer"].assignedToUserList,
    isEqual,
  );

  const taskGroupList = useSelector(
    (state: any) => state.taskGroupReducer.taskGroupList,
  );

  const externalUserGroupList = useSelector(
    (state: any) => state.externalUsersReducer.externalUserGroupList,
  );

  const attachLevelEnums = useSelector(
    (state: any) => state["userTaskListReducer"].attachLevelEnums,
    isEqual,
  );

  const externalUserList = useSelector(
    (state: any) =>
      state.externalUsersReducer.externalUserListForExternalUserGroup,
    isEqual,
  );

  const checkIfAssignedToExternalUser = (assignedToUserList) => {
    if (userTaskFormData.isAssignedToExternalUser) {
      let selectedExternalUser = externalUserList?.find(
        (x) => x.externalUserId === userTaskFormData.assignedToUserId,
      );
      if (selectedExternalUser?.externalUserId > 0) {
        selectedExternalUser.fullName =
          selectedExternalUser.externalUserName + ` (External User)`;
        selectedExternalUser.auditUserId = selectedExternalUser.externalUserId;
      }
      if (selectedExternalUser) assignedToUserList.push(selectedExternalUser);
    }
    if (userTaskFormData.isCompletedByExternalUser) {
      let selectedExternalUser = externalUserList?.find(
        (x) => x.externalUserId === userTaskFormData.completedByUsedId,
      );
      if (selectedExternalUser?.externalUserId > 0) {
        selectedExternalUser.fullName =
          selectedExternalUser.externalUserName + ` (External User)`;
        selectedExternalUser.auditUserId = selectedExternalUser.externalUserId;
      }
      if (
        selectedExternalUser &&
        !assignedToUserList.find(
          (x) => x.auditUserId === selectedExternalUser?.auditUserId,
        )
      )
        assignedToUserList.push(selectedExternalUser);
    }
    return assignedToUserList;
  };
  const onUserTaskStatusChanged = (e) => {
    if (e.event) {
      if (isStatusTypeCompleted(userTaskStatusList, e.value)) {
        userTaskFormData.completeInd = true;
        userTaskFormData.completedByUsedId = currentUserId;
        userTaskFormData.completionDate = new Date();
      } else {
        userTaskFormData.completeInd = false;
        userTaskFormData.completedByUsedId = null;
        userTaskFormData.completionDate = null;
      }
      dispatch({
        type: SET_USER_TASK_FORM_DATA,
        data: { userTaskFormData: { statusId: e.value } },
      });
    }
  };

  const checkIfAssignedToExternalUserGroup = (assignedToUserList) => {
    let selectedExternalUserGroup = externalUserGroupList?.find(
      (x) => x.externalUserGroupId === userTaskFormData.assignedToUserId,
    );
    if (selectedExternalUserGroup?.externalUserGroupId > 0) {
      selectedExternalUserGroup.fullName =
        selectedExternalUserGroup.externalUserGroupName +
        ` (External User Group)`;
      selectedExternalUserGroup.auditUserId =
        selectedExternalUserGroup.externalUserGroupId;
    }
    if (selectedExternalUserGroup)
      assignedToUserList.push(selectedExternalUserGroup);
    return assignedToUserList;
  };

  const checkIfAssignedToTaskGroup = (assignedToUserList) => {
    let selectedTaskGroup = taskGroupList?.find(
      (x) => x.taskGroupId === userTaskFormData.assignedToUserId,
    );
    if (selectedTaskGroup?.taskGroupId > 0) {
      selectedTaskGroup.fullName =
        selectedTaskGroup.taskGroupName + ` (Task Group)`;
      selectedTaskGroup.auditUserId = selectedTaskGroup.taskGroupId;
    }
    if (selectedTaskGroup) assignedToUserList.push(selectedTaskGroup);
    return assignedToUserList;
  };

  const getAssignedUserList = (auditgroupId, isFromAssignedToField) => {
    if (currentUser?.isTaskHQAccessEnabled || hasTaskGroupAccessOnly) {
      let assignedToUserList = cloneDeep(assignedToUserListFromAttachedLevel);
      if (
        userTaskFormData.assignedUserType == USER_TYPE.NORMAL &&
        userTaskFormData.assignedToUserId &&
        userTaskFormData.assignedToUserId > 0 &&
        !assignedToUserList?.find(
          (x) => x.auditUserId == userTaskFormData.assignedToUserId,
        )
      ) {
        assignedToUserList = checkIfAssignedToExternalUser(assignedToUserList);
      } else if (userTaskFormData.assignedUserType == USER_TYPE.TG) {
        assignedToUserList = checkIfAssignedToTaskGroup(assignedToUserList);
      } else if (userTaskFormData.assignedUserType == USER_TYPE.EG) {
        assignedToUserList = checkIfAssignedToExternalUserGroup(
          assignedToUserList,
        );
      }
        if (
            userTaskFormData.assignedToUserId &&
            userTaskFormData.assignedToUserId > 0 &&
            !assignedToUserList?.find(
              (x) => x.auditUserId == userTaskFormData.assignedToUserId,
            ) &&
            auditorList
          ) {
            var matchedAuditor = auditorList.find(
              (x) => x.auditUserId == userTaskFormData.assignedToUserId,
            );
            if (matchedAuditor) {
              assignedToUserList.push(matchedAuditor);
            }
          }
      return assignedToUserList || [];
    } else if (isMaintenanceTask(userTaskFormData, attachLevelEnums)) {
      let assignedToUserList = cloneDeep(assignedToUserListFromAttachedLevel);
      if (isFromAssignedToField == true) {
        if (taskGroupList?.length > 0 && hasTaskGroupAccess) {
          let taskGroup = { auditUserId: -3, fullName: "Task Group" };
          assignedToUserList.unshift(taskGroup);
        }
        if (externalUserGroupList?.length > 0) {
          let externalUserGroup = {
            auditUserId: -2,
            fullName: "External User Group",
          };
          assignedToUserList.unshift(externalUserGroup);
        }
      }
      if (externalUserList?.length > 0) {
        let externalUser = { auditUserId: -1, fullName: "External User" };
        assignedToUserList.unshift(externalUser);
      }
      if (userTaskFormData.assignedUserType == USER_TYPE.NORMAL) {
        assignedToUserList = checkIfAssignedToExternalUser(assignedToUserList);
      } else if (userTaskFormData.assignedUserType == USER_TYPE.EG) {
        assignedToUserList = checkIfAssignedToExternalUserGroup(
          assignedToUserList,
        );
      } else if (userTaskFormData.assignedUserType == USER_TYPE.TG) {
        assignedToUserList = checkIfAssignedToTaskGroup(assignedToUserList);
      }
      return assignedToUserList || [];
    } else if (auditorList) {
      var assignedToList = [];
      var len = auditorList.length;
      auditgroupId = auditgroupId
        ? auditgroupId
        : userTaskFormData.auditGroupId;
      if (isFromAssignedToField == true) {
        if (taskGroupList?.length > 0 && hasTaskGroupAccess) {
          let taskGroup = { auditUserId: -3, fullName: "Task Group" };
          assignedToList.unshift(taskGroup);
        }
        if (externalUserGroupList?.length > 0) {
          let externalUserGroup = {
            auditUserId: -2,
            fullName: "External User Group",
          };
          assignedToList.unshift(externalUserGroup);
        }
      }
      if (externalUserList?.length > 0) {
        let externalUser = { auditUserId: -1, fullName: "External User" };
        assignedToList.push(externalUser);
      }

      for (var i = 0, len = len; i < len; i++) {
        if (auditorList[i].auditGroupId === auditgroupId) {
          assignedToList.push(auditorList[i]);
        }
      }
      if (userTaskFormData.assignedUserType == USER_TYPE.NORMAL) {
        assignedToList = checkIfAssignedToExternalUser(assignedToList);
      } else if (userTaskFormData.assignedUserType == USER_TYPE.EG) {
        assignedToList = checkIfAssignedToExternalUserGroup(assignedToList);
      } else if (userTaskFormData.assignedUserType == USER_TYPE.TG) {
        assignedToList = checkIfAssignedToTaskGroup(assignedToList);
      }
      return assignedToList;
    }
  };

  return {
    onUserTaskStatusChanged,
    getAssignedUserList,
  };
}
